import { useEstimate } from '../context/EstimateContext'
import { currency, priceRange } from '../lib/format'
import { COMPANY, NEVER_COLD } from '../config/constants'
import Disclaimer from '../components/Disclaimer'
import { Check, Star, Sparkles, ChevronRight } from '../components/icons'

export default function VerdictStep() {
  const { estimate, unit, chooseOption } = useEstimate()

  if (!estimate) return null
  const { unitSummary, headline, rationale, options = [], factors = [], memberSavings } = estimate
  const lowConfidence =
    unit?.decode?.confidence === 'low' || unit?.decode?.confidence === 'manual' || unit?.needsConfirmation

  return (
    <div className="animate-fade-in">
      {/* Unit summary */}
      <div className="glass p-5">
        <p className="eyebrow text-brand-600">Your unit</p>
        <p className="mt-1.5 font-display text-lg font-bold leading-snug text-ink">{unitSummary}</p>
        {lowConfidence && (
          <p className="mt-2 text-xs leading-relaxed text-muted">
            We estimated the age from what we could read — your technician confirms the exact model
            and age on-site.
          </p>
        )}
      </div>

      {/* Honest recommendation */}
      <div className="mt-5">
        <h1 className="text-[26px] font-bold leading-tight text-ink">{headline}</h1>
        <p className="mt-2 text-sm leading-relaxed text-subink">{rationale}</p>
      </div>

      {/* Options */}
      <div className="mt-5 space-y-4">
        {options.map((opt, i) => (
          <OptionCard key={opt.type + i} option={opt} onChoose={() => chooseOption(opt.type)} />
        ))}
      </div>

      {/* What can move it within the range */}
      {factors.length > 0 && (
        <div className="mt-5 glass p-5">
          <p className="font-display text-sm font-semibold text-ink">
            What can move the price within the range
          </p>
          <ul className="mt-3 space-y-2">
            {factors.map((f, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-subink">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs leading-relaxed text-muted">
            Anything extra is shown to you with a photo for approval before it&apos;s done — never a
            surprise on the bill.
          </p>
        </div>
      )}

      {/* Never Cold membership hook */}
      <div className="mt-5 rounded-3xl bg-brand-grad p-[1.5px] shadow-glass">
        <div className="rounded-[22px] bg-white/90 p-4 backdrop-blur">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand-500" />
            <p className="font-display font-bold text-ink">
              {NEVER_COLD.name} members save <span className="text-grad">{currency(memberSavings)}</span>
            </p>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-muted">{NEVER_COLD.blurb}</p>
        </div>
      </div>

      <Disclaimer />

      <p className="mt-4 text-center text-xs text-muted">
        Questions first? Call {COMPANY.name} at{' '}
        <a href={`tel:${COMPANY.phone.replace(/[^\d+]/g, '')}`} className="font-semibold text-brand-600">
          {COMPANY.phone}
        </a>
      </p>
    </div>
  )
}

function OptionCard({ option, onChoose }) {
  const suffix = option.type === 'replace' ? 'installed' : 'total'
  const recommended = option.recommended

  const Inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          {recommended && (
            <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-brand-grad px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-glow">
              <Star className="h-3 w-3" />
              Recommended
            </span>
          )}
          <p className="eyebrow capitalize">{option.type}</p>
          <p className="mt-0.5 font-display font-semibold text-ink">{option.label}</p>
        </div>
      </div>

      <p className="mt-3 font-display text-[34px] font-extrabold leading-none tracking-tight">
        <span className={recommended ? 'text-grad' : 'text-ink'}>
          {priceRange(option.totalLow, option.totalHigh)}
        </span>
        <span className="ml-1.5 text-base font-medium text-muted">{suffix}</span>
      </p>

      <dl className="mt-4 space-y-1.5 border-t border-hairline pt-4 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted">{option.exactLineLabel}</dt>
          <dd className="font-semibold tabular-nums text-ink">{currency(option.exactPrice)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted">Installation</dt>
          <dd className="font-semibold tabular-nums text-ink">
            {priceRange(option.laborLow, option.laborHigh)}
          </dd>
        </div>
      </dl>

      <p className="mt-3 flex items-start gap-1.5 text-[12px] leading-snug text-muted">
        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-500" />
        Final price confirmed on-site before any work begins — never after.
      </p>

      <button
        onClick={onChoose}
        className={
          recommended
            ? 'btn-primary mt-4 flex items-center justify-center gap-1.5'
            : 'btn-secondary mt-4 flex items-center justify-center gap-1.5'
        }
      >
        Book this
        <ChevronRight className="h-4 w-4" />
      </button>
    </>
  )

  if (recommended) {
    return (
      <div className="rounded-[26px] bg-brand-grad p-[1.5px] shadow-glass-lg animate-pop-in">
        <div className="rounded-[24.5px] bg-white/95 p-5 backdrop-blur-xl">{Inner}</div>
      </div>
    )
  }
  return <div className="glass p-5">{Inner}</div>
}
